import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import {
  Package,
  ClipboardList,
  ClipboardCheck,
  ArrowLeftRight,
  PackageCheck,
  Boxes,
  Truck,
  FileBox,
  ShoppingCart,
  Layers,
  PackageOpen,
  Receipt,
  Activity,
  Tag,
  Percent,
  Settings2,
  DollarSign,
  Sparkles,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { RequirePermission } from '@/components/ProtectedRoute';
import { LocationSelector } from '@/components/location/LocationSelector';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { isPurchasingManagerEnabled } from '@/lib/organizationFeatures';

// Import sub-pages
import InventoryOverview from './InventoryOverview';
import InventoryItems from './InventoryItems';
import InventoryMovements from './InventoryMovements';
import InventoryCounts from './InventoryCounts';
import Suppliers from './Suppliers';
import PurchaseOrders from './PurchaseOrders';
import Receiving from './Receiving';
import GoodsRequests from './GoodsRequests';
import Transfers from './Transfers';
import GoodsTransfers from './GoodsTransfers';
import Fulfillments from './Fulfillments';
import SupplierInvoices from './SupplierInvoices';
import CashierCounts from './CashierCounts';
import ProductsList from '../Products/ProductsList';
import Categories from '../Products/Categories';
import Pricing from '../Products/Pricing';
import Modifiers from '../Products/Modifiers';
import PromotionsList from '../Promotions/PromotionsList';
import PromotionForm from '../Promotions/PromotionForm';
import PurchasingManager from './PurchasingManager';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  description?: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: 'CATALOG',
    items: [
      {
        id: 'overview',
        label: 'Inventory',
        icon: Package,
        path: '/stock-control/overview',
        description: 'View and manage stock levels',
      },
      {
        id: 'categories',
        label: 'Categories',
        icon: Tag,
        path: '/stock-control/categories',
        description: 'Organize products into categories',
      },
      {
        id: 'promotions',
        label: 'Promotions',
        icon: Percent,
        path: '/stock-control/promotions',
        description: 'Manage discounts and special offers',
      },
      {
        id: 'modifiers',
        label: 'Modifiers',
        icon: Settings2,
        path: '/stock-control/modifiers',
        description: 'Customize items with add-ons and options',
      },
      {
        id: 'pricing',
        label: 'Pricing',
        icon: DollarSign,
        path: '/stock-control/pricing',
        description: 'Set prices and location overrides',
      },
    ],
  },
  {
    title: 'INVENTORY',
    items: [
      {
        id: 'movements',
        label: 'Movements',
        icon: Activity,
        path: '/stock-control/movements',
        description: 'Track all inventory movements',
      },
      {
        id: 'raw-items',
        label: 'Raw Materials',
        icon: Layers,
        path: '/stock-control/raw-items',
        description: 'Manage ingredients and supplies',
      },
      {
        id: 'counts',
        label: 'Inventory Counts',
        icon: ClipboardList,
        path: '/stock-control/counts',
        description: 'Schedule and perform stock counts',
      },
      {
        id: 'cashier-counts',
        label: 'Cashier Counts',
        icon: ClipboardCheck,
        path: '/stock-control/cashier-counts',
        description: 'Shareable cashier stock checks',
      },
    ],
  },
  {
    title: 'PURCHASING',
    items: [
      {
        id: 'purchasing-manager',
        label: 'Purchasing Manager',
        icon: Sparkles,
        path: '/stock-control/purchasing-manager',
        description: 'Forecast demand and review purchasing recommendations',
      },
      {
        id: 'suppliers',
        label: 'Suppliers',
        icon: Truck,
        path: '/stock-control/suppliers',
        description: 'Manage vendors and suppliers',
      },
      {
        id: 'purchase-orders',
        label: 'Stock Control',
        icon: ShoppingCart,
        path: '/stock-control/purchase-orders',
        description: 'Orders, transfers, and returns',
      },
      {
        id: 'receiving',
        label: 'Receiving',
        icon: PackageOpen,
        path: '/stock-control/receiving',
        description: 'Receive goods from suppliers',
      },
      {
        id: 'invoices',
        label: 'Supplier Invoices',
        icon: Receipt,
        path: '/stock-control/invoices',
        description: 'Track supplier invoices and payments',
      },
    ],
  },
  {
    title: 'TRANSFERS',
    items: [
      {
        id: 'goods-requests',
        label: 'Goods Requests',
        icon: FileBox,
        path: '/stock-control/goods-requests',
        description: 'Request stock from other locations',
      },
      {
        id: 'transfers',
        label: 'Transfers',
        icon: ArrowLeftRight,
        path: '/stock-control/transfers',
        description: 'Transfer stock between locations',
      },
      {
        id: 'fulfillments',
        label: 'Fulfillments',
        icon: PackageCheck,
        path: '/stock-control/fulfillments',
        description: 'Track order fulfillments',
      },
    ],
  },
];

export default function StockControl() {
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { organization, profile } = useAuth();
  const purchasingManagerEnabled = isPurchasingManagerEnabled(organization?.settings, profile?.role);
  const canViewPurchasingManager = purchasingManagerEnabled
    && hasAnyPermission(['inventory.purchasing.view', 'inventory.purchase_orders.view']);

  const visibleNavSections = navSections
    .map((section) => {
      return {
        ...section,
        items: section.items.filter((item) => {
          if (item.id === 'purchasing-manager') return canViewPurchasingManager;
          if (section.title !== 'CATALOG') return true;
          if (item.id === 'overview') {
            return hasAnyPermission(['inventory.view', 'menu.view']);
          }
          return hasPermission('menu.view');
        }),
      };
    })
    .filter((section) => section.items.length > 0);

  const header = (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          <Boxes className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-semibold tracking-tight">
            Inventory
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage catalog, inventory, counts, transfers, and fulfillments
          </p>
        </div>
      </div>
      <LocationSelector showAllOption={true} />
    </div>
  );

  return (
    <DashboardLayout header={header} fullWidth>
      <div className="flex gap-6 -mt-2 min-w-0 overflow-hidden">
        {/* Secondary Sidebar Navigation */}
        <aside className="w-56 shrink-0">
          <nav className="sticky top-4 space-y-1">
            {visibleNavSections.map((section) => (
              <div key={section.title} className="space-y-1">
                <div className="px-3 pt-3 pb-1 text-[11px] font-semibold tracking-wide text-muted-foreground">
                  {section.title}
                </div>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                          'hover:bg-muted/70',
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:text-foreground'
                        )
                      }
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0 overflow-x-auto">
          <Routes>
            <Route path="overview" element={<InventoryOverview />} />
            <Route
              path="manage"
              element={
                <RequirePermission permission="menu.view">
                  <ProductsList />
                </RequirePermission>
              }
            />
            <Route
              path="categories"
              element={
                <RequirePermission permission="menu.view">
                  <Categories />
                </RequirePermission>
              }
            />
            <Route
              path="promotions"
              element={
                <RequirePermission permission="menu.view">
                  <PromotionsList />
                </RequirePermission>
              }
            />
            <Route
              path="promotions/new"
              element={
                <RequirePermission permission="menu.view">
                  <PromotionForm />
                </RequirePermission>
              }
            />
            <Route
              path="promotions/edit/:id"
              element={
                <RequirePermission permission="menu.view">
                  <PromotionForm />
                </RequirePermission>
              }
            />
            <Route
              path="modifiers"
              element={
                <RequirePermission permission="menu.view">
                  <Modifiers />
                </RequirePermission>
              }
            />
            <Route
              path="pricing"
              element={
                <RequirePermission permission="menu.view">
                  <Pricing />
                </RequirePermission>
              }
            />
            <Route path="movements" element={<InventoryMovements />} />
            <Route path="raw-items" element={<InventoryItems />} />
            <Route path="counts/*" element={<InventoryCounts />} />
            <Route path="cashier-counts" element={<CashierCounts />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route
              path="purchasing-manager"
              element={canViewPurchasingManager ? <PurchasingManager /> : <Navigate to="/stock-control/overview" replace />}
            />
            <Route path="purchase-orders/*" element={<PurchaseOrders />} />
            <Route path="receiving" element={<Receiving />} />
            <Route path="invoices/*" element={<SupplierInvoices />} />
            <Route path="goods-requests/*" element={<GoodsRequests />} />
            <Route path="transfers/*" element={<Transfers />} />
            <Route path="goods-transfers/*" element={<GoodsTransfers />} />
            <Route path="fulfillments/*" element={<Fulfillments />} />
            <Route path="*" element={<Navigate to="/stock-control/overview" replace />} />
          </Routes>
        </div>
      </div>
    </DashboardLayout>
  );
}
